import type {
  MessengerDesktopNotifier,
  MessengerDesktopPermission,
  MessengerNotification,
} from '@crm/messenger';

interface NotificationLike {
  onclick: (() => void) | null;
  close?: () => void;
}

interface NotificationClassLike {
  readonly permission: string;
  requestPermission(): Promise<string>;
  new (title: string, options?: Record<string, unknown>): NotificationLike;
}

interface NavigatorLike {
  readonly serviceWorker?: {
    getRegistration?: () => Promise<
      { showNotification?: (t: string, o?: Record<string, unknown>) => Promise<void> } | undefined
    >;
  };
}

export interface WebNotificationOptions {
  readonly notificationClass?: NotificationClassLike;
  readonly navigatorImpl?: NavigatorLike;
  readonly onNavigate?: (actionUrl: string) => void;
}

function defaultNotificationClass(): NotificationClassLike | undefined {
  return typeof globalThis !== 'undefined' &&
    'Notification' in globalThis
    ? (globalThis['Notification'] as unknown as NotificationClassLike)
    : undefined;
}

export class WebNotificationAdapter implements MessengerDesktopNotifier {
  private readonly notificationClass: NotificationClassLike | undefined;
  private readonly navigatorImpl: NavigatorLike | undefined;

  constructor(private readonly options: WebNotificationOptions = {}) {
    this.notificationClass =
      options.notificationClass ?? defaultNotificationClass();
    this.navigatorImpl =
      options.navigatorImpl ??
      (typeof globalThis !== 'undefined'
        ? (globalThis['navigator'] as NavigatorLike | undefined)
        : undefined);
  }

  get supported(): boolean {
    return Boolean(this.notificationClass);
  }

  permission(): MessengerDesktopPermission {
    const permission = this.notificationClass?.permission;
    return permission === 'granted' ||
      permission === 'denied' ||
      permission === 'default'
      ? permission
      : 'unsupported';
  }

  async requestPermission(): Promise<MessengerDesktopPermission> {
    const notificationClass = this.notificationClass;
    if (!notificationClass) return 'unsupported';
    const permission = await notificationClass.requestPermission();
    return permission === 'granted' ||
      permission === 'denied' ||
      permission === 'default'
      ? permission
      : 'unsupported';
  }

  async show(
    notification: MessengerNotification,
    onActivate: () => void,
  ): Promise<boolean> {
    const notificationClass = this.notificationClass;
    if (!notificationClass) return false;
    const options = {
      body: notification.message,
      tag: `mello-notification-${notification.id}`,
      data: { id: notification.id, actionUrl: notification.actionUrl },
    };
    try {
      const instance = new notificationClass(notification.title, options);
      instance.onclick = () => {
        this.options.onNavigate?.(notification.actionUrl);
        onActivate();
        instance.close?.();
      };
      return true;
    } catch {
      const registration =
        await this.navigatorImpl?.serviceWorker?.getRegistration?.();
      if (!registration?.showNotification) return false;
      try {
        await registration.showNotification(notification.title, options);
        return true;
      } catch {
        return false;
      }
    }
  }
}
